import TypographyStyles from '@Styles/typography.scss';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import _sortBy from 'lodash/sortBy';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IconButton } from '@Components/IconButton';
import { IReworkReason } from '@Data/qualityControl/Interfaces';
import { selectedAnalysisEntityState } from '@Data/qualityControl/Reducer';
import { ReworkComment } from './ReworkComment';
import Styles from './ReworkTable.scss';

interface IReworkTableProps {
    onEdit: (reworkReason: IReworkReason) => void;
    onDelete: (reworkReason: IReworkReason) => void;
}

export const ReworkTable = (props: IReworkTableProps) => {
    const selectedAnalysisEntity = useSelector(selectedAnalysisEntityState);
    const reworkReasons = selectedAnalysisEntity?.reworkReasons ?? [];

    const sortedReasons = useMemo(() => {
        return !_isNil(reworkReasons) && reworkReasons.length > 0
            ? _sortBy(reworkReasons, 'description')
            : [];
    }, [reworkReasons]);

    const columnHelper = createColumnHelper<IReworkReason>();

    const columns = [
        columnHelper.accessor('description', {
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('comment', {
            cell: (info) => <ReworkComment comment={info.getValue()} />,
        }),
        columnHelper.accessor('id', {
            cell: (info) => {
                const reworkReason = info.row.original;
                return (
                    <div className={Styles.tableActionButtonsContainer}>
                        <IconButton
                            tooltip={'Edit'}
                            icon={<EditIcon fontSize="small" />}
                            style={{ background: 'transparent' }}
                            onClick={() => props.onEdit(reworkReason)}
                        />
                        <IconButton
                            tooltip={'Delete'}
                            icon={<DeleteIcon fontSize="small" />}
                            style={{ background: 'transparent' }}
                            onClick={() => props.onDelete(reworkReason)}
                        />
                    </div>
                );
            },
            size: 45,
        }),
    ];

    const table = useReactTable({
        data: sortedReasons,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (sortedReasons.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <span className={TypographyStyles.bodyCopySmall}>
                    No rework reasons have been added
                </span>
            </div>
        );
    }

    return (
        <div className={Styles.tableContainer}>
            <table className={Styles.table}>
                <thead style={{ visibility: 'hidden' }}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <th
                                        key={`${
                                            header.id
                                        }-${Math.random().toString(16)}`}
                                        colSpan={header.colSpan}
                                        style={{ width: header.getSize() }}
                                    ></th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <td id={cell.id} key={cell.id}>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
